import React, { useEffect, useState, useMemo } from "react";
import styles from "./styles.css";
import { NUMBER, stringConstants } from "../../../constants/app-constants";
import PropTypes from "prop-types";
import debounce from "../../../utils/helpers/debounce";
import { STEP_NAMES } from "../pre-delivery-inspection-vehicle-info-screen/constants";
import { SearchInput } from "@cars24/turbo-web/lib/search-input";
import { Label } from "@cars24/turbo-web/lib/label";
import { useTokens } from "@cars24/turbo-web/lib/theme-provider";
import { BodyText } from "@cars24/turbo-web/lib/body-text";
import { yieldToMain } from "../../../utils/helpers/yield-to-main";
import { sendCustomGaEvents } from "../../../tracking";
import { pdiBrandClicked } from "../../../constants/tracking-constants/pre-delivery-inspection";
import IconSpinner from "../../shared/icon-spinner";

const BrandList = ({
    brandList,
    fetchBrandsConnect,
    brand: selectedBrand,
    setDetailsConnect
}) => {
    const { tokens } = useTokens();
    const [isLoading, setIsLoading] = useState(false);
    const [brands, setBrands] = useState(brandList);
    const [searchText, setSearchText] = useState("");
    const { id: selectedBrandId = "" } = selectedBrand || {};

    useEffect(() => {
        if ((brandList || []).length > NUMBER.ZERO) {
            setIsLoading(false);
        } else {
            fetchBrandsConnect()
                .then(() => {
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                });
        }

        setBrands(brandList);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brandList]);

    const filterBrandListOnSearchQuery = debounce((searchQuery) => {
        setBrands(
            brandList.filter((item) =>
                item.displayName.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, NUMBER.FOUR_HUNDRED);

    const handleOnChange = (searchQuery) => {
        setSearchText(searchQuery);
        filterBrandListOnSearchQuery(searchQuery);
    };

    const handleSelectBrand = async ({ brandName, brandId }) => {
        setDetailsConnect({
            [STEP_NAMES.Brand]: { name: brandName, id: brandId },
            ...(selectedBrandId !== brandId && {[STEP_NAMES.Model]: {}}),
            activeAccordionStepIndex: NUMBER.ONE,
            ownerName: ""
        });
        await yieldToMain();
        sendCustomGaEvents({
            ...pdiBrandClicked,
            eventLabel: brandName
        });
    };

    const handleSelectBrandClick = (brand) => {
        handleSelectBrand({
            brandName: brand.displayName,
            brandId: brand.make_id
        });
    };

    const popularBrands = useMemo(
        () => brands?.filter((popularBrand) => popularBrand.is_popular === stringConstants.YES && popularBrand.logo),
        [brands]
    );

    if (isLoading) return <div styleName={"styles.loadingIcon"}><IconSpinner/></div>;

    return (
        <div>
            <div styleName={"styles.searchField"}>
                <SearchInput placeholder="Search by Brand"  value={searchText}  onChange={(e) => handleOnChange(e.target.value)} showCross={false} />
            </div>
            <div styleName={"styles.popularBrands"}>
                {((popularBrands && popularBrands.length) || (brands && brands.length)) ?
                    <React.Fragment>
                        <div styleName={"styles.scrollWrapper"}>
                            <div styleName={"styles.subheadingContainer"}>
                                <Label level={NUMBER.FOUR} color={tokens.text_default_text_secondary} fontWeight="regular">Popular Brands</Label>
                            </div>
                            <ul styleName={"styles.brandLogo"}>
                                {popularBrands && popularBrands.map((brand) => (
                                    <li
                                        styleName={
                                            brand.make_id === selectedBrandId ? "styles.selectedState" : ""
                                        }
                                        onClick={() => handleSelectBrandClick(brand)}
                                        key={brand.make_id}
                                    >
                                        <img alt={brand.displayName} src={brand && brand.logo} />
                                    </li>
                                ))}
                            </ul>
                            <div styleName={"styles.allBrands"}>
                                <div styleName={"styles.subheadingContainer"}>
                                    <Label level={NUMBER.FOUR} color={tokens.text_default_text_secondary} fontWeight="regular">All Brands</Label>
                                </div>
                                <ul styleName={"styles.brandsList"}>
                                    {brands && brands.map((brand, index) => (
                                        <li key={`brand-${index}`} onClick={() => handleSelectBrandClick(brand)}>
                                            <BodyText level={NUMBER.THREE}>{brand.displayName}</BodyText>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </React.Fragment>
                    :
                    <Label level={NUMBER.FOUR} color={tokens.text_default_text_secondary} fontWeight="regular">No results found</Label>}
            </div>
        </div>
    );
};

export default BrandList;

BrandList.propTypes = {
    brandList: PropTypes.array,
    fetchBrandsConnect: PropTypes.func,
    brand: PropTypes.object,
    setDetailsConnect: PropTypes.func
};
